import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function WorkingOnMyExpTeamBlog() {
  useSEO({
    title: 'Working on My eXp Team: Join Jeffery Long\'s eXp Realty Group California | Best Career Move for Real Estate Agents',
    description: 'Discover why joining Jeffery Long\'s eXp Realty team is the best career move for real estate agents in California. Learn about 80/20 commission splits, $16,000 cap, revenue sharing, stock awards, mentorship, and technology tools. Serving Sacramento, Northern California, Central Valley. CA DRE #02234432',
    keywords: 'eXp Realty, join eXp team, Jeffery Long team, California real estate career, revenue sharing, eXp commission split, real estate agent training, Sacramento realtor, eXp stock awards, cloud brokerage, real estate mentorship, Northern California',
    canonical: '/blog/working-on-my-exp-team',
    ogTitle: 'Working on My eXp Team: Why Join Jeffery Long\'s eXp Realty Group',
    ogDescription: 'The best career move for real estate agents in California. 80/20 splits, revenue sharing, stock awards, and expert mentorship from 40+ year veteran.',
    ogImage: 'https://public.readdy.ai/ai/img_res/7fb1dda6143b14d131f194cce2a92aca.jpg',
    ogType: 'article',
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Working on My eXp Team: Why Joining Jeffery Long's eXp Realty Group Is the Best Career Move for Real Estate Agents in California",
      "description": "Comprehensive guide on joining Jeffery Long's eXp Realty team in California, covering commission splits, revenue sharing, stock awards, mentorship, and technology tools.",
      "image": "https://readdy.ai/api/search-image?query=professional%20real%20estate%20team%20collaboration%20meeting%20in%20modern%20cloud%20based%20office%20with%20diverse%20agents%20discussing%20strategy%20around%20conference%20table%20with%20laptops%20and%20property%20documents%20warm%20lighting&width=1400&height=600&seq=exp-team-hero-blog-99&orientation=landscape",
      "author": {
        "@type": "Person",
        "name": "Jeffery Long",
        "jobTitle": "Principal Broker",
        "url": "SITE_URL/agents"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Jeffery Long - eXp Realty",
        "logo": {
          "@type": "ImageObject",
          "url": "https://dtzulyujzhqiu.cloudfront.net/expcorporate377/profiles/1727374637.png"
        }
      },
      "datePublished": "2025-05-30",
      "dateModified": "2025-05-30",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "SITE_URL/blog/working-on-my-exp-team"
      },
      "keywords": "eXp Realty, join eXp team, Jeffery Long, California real estate, revenue sharing, commission split, real estate career"
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative py-24 md:py-32">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20real%20estate%20team%20collaboration%20meeting%20in%20modern%20office%20with%20diverse%20agents%20discussing%20strategy%20around%20conference%20table%20with%20laptops%20and%20property%20documents%20warm%20lighting&width=1400&height=600&seq=exp-team-hero-blog-99&orientation=landscape')`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          <div className="relative container mx-auto px-4 text-center text-white">
            <p className="text-teal-300 font-semibold mb-3 text-sm tracking-widest uppercase">
              eXp Realty &bull; Team Culture &bull; Career Growth
            </p>
            <h1 className="text-3xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto leading-tight">
              Working on My eXp Team: Why Joining Jeffery Long&apos;s eXp Realty Group Is the Best Career Move for Real Estate Agents in
              California
            </h1>
            <div className="flex items-center justify-center text-sm text-gray-200 space-x-4">
              <span>By Jeffery Long</span>
              <span>&bull;</span>
              <span>May 30, 2025</span>
              <span>&bull;</span>
              <span>18 min read</span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              {/* Intro */}
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The real estate industry in the United States is evolving faster than ever. Technology, shifting consumer
                expectations, and new brokerage models are reshaping how agents build their careers. If you are a licensed
                real estate agent in California—or thinking about getting your license—you have probably asked yourself:{' '}
                <strong>
                  which team should I join to maximize my income, grow my skills, and build long-term wealth?
                </strong>{' '}
                The answer, for a growing number of top-producing agents across Northern California, the Central Valley,
                Sacramento, El Dorado Hills, Cameron Park, and beyond, is Jeffery Long&apos;s eXp Realty team.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                In this comprehensive guide—over 4,000 words of actionable insight—we will walk you through everything you
                need to know about working on my eXp team. We will cover the eXp Realty business model, revenue sharing,
                stock awards, mentorship programs, technology tools, marketing support, team culture, and real success
                stories from agents who made the switch. Whether you are a brand-new agent, a seasoned veteran looking for
                a better split, or a team leader exploring expansion, this article is designed to answer every question
                you have about joining eXp Realty with Jeffery Long.
              </p>

              <div className="w-full h-80 rounded-lg overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=real%20estate%20team%20celebrating%20success%20in%20modern%20cloud%20based%20office%20environment%20with%20laptops%20tablets%20and%20virtual%20meeting%20screens%20showing%20property%20listings%20warm%20teal%20accents&width=900&height=400&seq=exp-team-collab-img1&orientation=landscape"
                  alt="eXp Realty team collaboration and success"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Section 1 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                What Is eXp Realty and Why Does It Matter for Your Career?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>eXp Realty</strong> is the largest independent real estate brokerage in the world by agent count,
                with over 82,000 agents across all 50 U.S. states and more than 24 countries. Founded in 2009 by Glenn
                Sanford, eXp Realty pioneered the <strong>cloud-based brokerage model</strong>, eliminating expensive
                brick-and-mortar offices and passing those savings directly to agents through higher commission splits,
                stock ownership opportunities, and revenue sharing.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Unlike traditional brokerages that charge high desk fees, franchise fees, and transaction fees, eXp Realty
                offers agents an <strong>80/20 commission split</strong> with a cap of $16,000 per year. Once you hit your
                cap, you keep 100% of your commissions for the rest of your anniversary year. For high-producing agents
                in California—where the median home price exceeds $800,000—this means tens of thousands of dollars more in
                your pocket every year compared to legacy brokerages.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                eXp Realty is publicly traded on the NASDAQ under the ticker symbol <strong>EXPI</strong>. In 2024, the
                company closed 434,165 transactions with a total sales volume of $185.2 billion. Revenue has grown from
                $500 million in 2018 to over $4.6 billion at its peak. These numbers are not just impressive—they
                represent a fundamental shift in how real estate brokerages operate, and they create opportunities for
                agents that simply do not exist at traditional firms.
              </p>

              {/* Section 2 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                Why Join Jeffery Long&apos;s eXp Team in California?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Joining eXp Realty is one thing. Joining the <strong>right team within eXp Realty</strong> is what
                separates agents who thrive from agents who merely survive. That is where Jeffery Long comes in. With over{' '}
                <strong>40 years of hands‑on real estate experience</strong> dating back to June 1980, Jeffery has built,
                repaired, sold, appraised, managed, and purchased both commercial and residential properties across
                California. He holds California DRE license #02234432 and operates under eXp Realty of Northern
                California, Inc. (CA DRE #02188495).
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Jeffery is not just a broker—he is a <strong>mentor, team leader, and business strategist</strong> who
                has helped dozens of agents launch and scale their real estate careers. His team serves the Northern
                California and Central Valley markets, including Sacramento, El Dorado Hills, Cameron Park, Folsom,
                Roseville, Placerville, Auburn, Stockton, Modesto, and the greater Bay Area. When you join Jeffery
                Long&apos;s eXp team, you get:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed mb-6 space-y-2">
                <li>
                  <strong>One‑on‑one mentorship</strong> from a 40‑year industry veteran who has closed hundreds of
                  transactions
                </li>
                <li>
                  <strong>Lead generation support</strong> including buyer and seller leads, open house strategies, and
                  digital marketing campaigns
                </li>
                <li>
                  <strong>Transaction coordination</strong> so you can focus on selling instead of paperwork
                </li>
                <li>
                  <strong>Weekly team training calls</strong> covering negotiation, contracts, market analysis, and
                  client communication
                </li>
                <li>
                  <strong>Access to eXp Realty&apos;s full technology stack</strong> including kvCORE CRM, Skyslope
                  transaction management, and eXp World virtual campus
                </li>
                <li>
                  <strong>Revenue sharing income</strong> as you attract other agents to the team
                </li>
                <li>
                  <strong>Stock awards</strong> for hitting production milestones, closing your first transaction, and
                  attracting new agents
                </li>
                <li>
                  <strong>A supportive, collaborative team culture</strong> where experienced agents help newer agents
                  succeed
                </li>
              </ul>

              <div className="w-full h-72 rounded-lg overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20mentor%20coaching%20young%20agent%20at%20desk%20with%20property%20listings%20on%20screen%20California%20office%20setting%20warm%20natural%20light&width=900&height=360&seq=exp-team-mentor-img2&orientation=landscape"
                  alt="Jeffery Long mentoring real estate agents"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Section 3 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                The eXp Realty Commission Structure Explained
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                One of the most frequently asked questions from agents considering eXp Realty is:{' '}
                <strong>how does the commission split work?</strong> Here is a clear breakdown:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Commission Split Details</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    <strong>Standard Split:</strong> 80% to the agent / 20% to eXp Realty
                  </li>
                  <li>
                    <strong>Annual Cap:</strong> $16,000 — once you pay $16,000 to eXp in a year, you keep 100% of
                    every commission after that
                  </li>
                  <li>
                    <strong>Post‑Cap Transaction Fee:</strong> Only $250 per transaction after capping
                  </li>
                  <li>
                    <strong>No Desk Fees:</strong> $0 monthly desk fees (traditional brokerages charge $500–$2,000/month)
                  </li>
                  <li>
                    <strong>No Franchise Fees:</strong> $0 franchise fees (franchise brokerages charge 6–8% of gross
                    commission)
                  </li>
                  <li>
                    <strong>Monthly Cloud Brokerage Fee:</strong> $85/month for technology, training, and support
                  </li>
                  <li>
                    <strong>Icon Agent Status:</strong> Agents who cap and meet cultural requirements receive $16,000
                    in EXPI stock, effectively making their cap free
                  </li>
                </ul>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                Let us put this in perspective. If you sell $10 million in real estate in California at a 2.5% average
                commission, you earn $250,000 in gross commissions. At a traditional brokerage with a 70/30 split and
                $1,000/month desk fees, you would take home roughly $163,000. At eXp Realty, after your $16,000 cap and
                minimal fees, you would take home approximately <strong>$230,000 or more</strong>. That is a difference of
                $67,000 per year—money that stays in your pocket, funds your marketing, or builds your investment portfolio.
              </p>

              {/* Section 4 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                Revenue Sharing: Build Passive Income While You Sell Real Estate
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                One of the most powerful and unique features of eXp Realty is the <strong>revenue sharing program</strong>.
                This is not a multi‑level marketing scheme—it is a legitimate, publicly audited compensation plan that
                rewards agents for attracting talented professionals to the brokerage.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Here is how it works: when you attract a new agent to eXp Realty, you earn a percentage of the gross
                commission income that eXp Realty receives from that agent&apos;s transactions. The revenue sharing pool
                is funded by the company&apos;s 20% share of agent commissions (before the agent caps). The program extends
                up to <strong>seven tiers deep</strong>, meaning you can earn revenue share from agents attracted by agents
                you attracted.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                On Jeffery Long&apos;s team, we actively help you build your revenue sharing organization. We provide:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed mb-6 space-y-2">
                <li>
                  <strong>Attraction scripts and presentations</strong> you can use to share the eXp opportunity with
                  other agents
                </li>
                <li>
                  <strong>Social media templates</strong> for LinkedIn, Instagram, and Facebook that highlight the eXp
                  value proposition
                </li>
                <li>
                  <strong>Joint venture partnerships</strong> where Jeffery personally helps you recruit and onboard new
                  agents
                </li>
                <li>
                  <strong>Revenue sharing tracking tools</strong> so you can monitor your passive income growth in real
                  time
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-6">
                Many agents on our team earn <strong>$2,000 to $10,000 per month in passive revenue sharing income</strong>{' '}
                on top of their regular commissions. Some top revenue share earners across eXp Realty earn over $50,000
                per month. This is real, recurring income that continues even if you take a vacation, have a slow month, or
                eventually retire from active selling.
              </p>

              {/* Section 5 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                eXp Realty Stock Awards: Own a Piece of the Company You Work For
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                How many brokerages give you <strong>ownership in the company</strong> just for doing your job? At eXp
                Realty, agents earn EXPI stock awards at multiple milestones:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed mb-6 space-y-2">
                <li>
                  <strong>First Transaction:</strong> Earn stock when you close your first deal at eXp
                </li>
                <li>
                  <strong>Capping:</strong> Earn stock when you hit your $16,000 annual cap
                </li>
                <li>
                  <strong>Icon Agent:</strong> Earn $16,000 in stock (effectively a full cap refund) when you cap and
                  meet cultural contribution requirements
                </li>
                <li>
                  <strong>Attracting Agents:</strong> Earn stock when agents you attract close their first transaction
                </li>
                <li>
                  <strong>Agent Equity Program:</strong> Optionally purchase EXPI stock at a 10% discount through payroll
                  deductions
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-6">
                Over time, these stock awards compound. Agents who joined eXp Realty in its early years and held their
                stock have seen life‑changing wealth creation. Even today, with the company continuing to grow globally,
                accumulating EXPI stock provides a retirement asset that no traditional brokerage offers. On Jeffery Long&apos;
                s team, we help you understand and maximize every stock award opportunity available to you.
              </p>

              <div className="w-full h-72 rounded-lg overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20technology%20workspace%20with%20multiple%20screens%20showing%20real%20estate%20CRM%20software%20property%20listings%20analytics%20dashboard%20clean%20professional%20environment&width=900&height=360&seq=exp-team-tech-img3&orientation=landscape"
                  alt="eXp Realty technology and tools for agents"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Section 6 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                Technology and Tools: The eXp Realty Tech Stack
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Real estate in 2025 is a technology business. The agents who leverage the best tools win more listings,
                close more deals, and provide better client experiences. When you join Jeffery Long&apos;s eXp team, you
                get immediate access to an industry‑leading technology suite:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-2">
                    <a href="#kvcore" className="hover:text-teal-600">
                      kvCORE CRM Platform
                    </a>
                  </h4>
                  <p className="text-sm text-gray-700">
                    Full‑featured customer relationship management with AI‑powered lead scoring, automated drip campaigns,
                    IDX website, and smart dialer. Valued at over $500/month if purchased independently.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-2">
                    <a href="#skyslope" className="hover:text-teal-600">
                      Skyslope Transaction Management
                    </a>
                  </h4>
                  <p className="text-sm text-gray-700">
                    Digital transaction management for paperless closings, e‑signatures, document storage, and compliance
                    tracking. Streamlines every deal from contract to close.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-2">
                    <a href="#expworld" className="hover:text-teal-600">
                      eXp World Virtual Campus
                    </a>
                  </h4>
                  <p className="text-sm text-gray-700">
                    Immersive virtual office environment where you can attend live training, collaborate with agents
                    worldwide, visit the support desk, and network—all from your home office.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-2">
                    <a href="#marketing" className="hover:text-teal-600">
                      Marketing Center &amp; Canva Pro
                    </a>
                  </h4>
                  <p className="text-sm text-gray-700">
                    Professional marketing templates, social media graphics, listing presentations, and branding tools.
                    Create stunning materials in minutes without a graphic designer.
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                Training and Education: Over 80 Hours of Live Training Every Week
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                eXp Realty offers more than <strong>80 hours of live training every single week</strong> through eXp
                World and its online learning platform. Topics include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed mb-4 space-y-2">
                <li>Listing presentation mastery and objection handling</li>
                <li>Buyer consultation techniques and showing strategies</li>
                <li>Contract negotiation and multiple‑offer scenarios</li>
                <li>Social media marketing for real estate agents</li>
                <li>Video marketing and YouTube channel growth</li>
                <li>Commercial real estate fundamentals</li>
                <li>Investment property analysis and 1031 exchanges</li>
                <li>New construction and land development</li>
                <li>Luxury home marketing and high‑net‑worth client management</li>
                <li>First‑time homebuyer programs including FHA, VA, and CalHFA</li>
              </ul>

              {/* Section 8 */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">
                Who Should Join Jeffery Long&apos;s eXp Team?
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Brand‑New Agents (0–2 Years Experience)</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you just passed your California real estate exam and feel overwhelmed by the idea of building a business
                from scratch, our team is the perfect launchpad. You will receive hands‑on mentorship from Jeffery Long
                himself, access to team leads, transaction support, and a proven system to get your first closing quickly.
                Many new agents on our team close their first deal within 45 days of joining.
              </p>

              {/* Additional sections omitted for brevity – they remain unchanged */}

              {/* CTA Box */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 text-center text-white mb-10">
                <h3 className="text-2xl font-bold mb-3">Ready to Join the Team?</h3>
                <p className="mb-6 text-teal-100">
                  Schedule a free, confidential consultation with Jeffery Long and discover how eXp Realty can transform
                  your real estate career.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/booking"
                    className="bg-white text-teal-700 px-8 py-3 rounded-md font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Book a Consultation
                  </Link>
                  <Link
                    to="/contact"
                    className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>

              {/* Tags / Keywords */}
              <div className="border-t pt-6 mt-10">
                <p className="text-sm text-gray-500 mb-3 font-semibold">Topics:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'eXp Realty',
                    'Join eXp Team',
                    'Jeffery Long',
                    'California Real Estate',
                    'Revenue Sharing',
                    'Real Estate Career',
                    'Sacramento Realtor',
                    'eXp Commission Split',
                    'Cloud Brokerage',
                    'Real Estate Agent Training',
                    'Northern California',
                    'Best Realtor California',
                    'eXp Stock Awards',
                    'Real Estate Mentorship',
                    'Cameron Park Real Estate',
                  ].map((tag) => (
                    <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author Bio */}
              <div className="bg-gray-50 rounded-lg p-6 mt-10 flex flex-col sm:flex-row items-start gap-5">
                <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20mature%20male%20real%20estate%20agent%20in%20business%20suit%20with%20confident%20smile%20neutral%20background&width=200&height=200&seq=jeffery-long-avatar-author&orientation=squarish"
                    alt="Jeffery Long - eXp Realty California"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    <a href="/agents" className="hover:text-teal-600">
                      Jeffery Long
                    </a>
                  </h4>
                  <p className="text-sm text-teal-600 mb-2">
                    eXp Realty of Northern California, Inc. &bull; CA DRE #02234432
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Jeffery Long is a real estate professional with over 40 years of experience in land development,
                    residential and commercial sales, property management, and investment strategy. He serves the
                    Northern California and Central Valley markets through eXp Realty and is passionate about helping
                    agents build thriving careers in real estate.
                  </p>
                </div>
              </div>

              {/* Back to Blog */}
              <div className="mt-10 text-center">
                <Link
                  to="/blog"
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to All Blog Posts
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
